import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "wouter";
import { Form, Formik } from "formik";
import { CalendarDays } from "lucide-react";

import { useQueryState } from "./use-query-state";
import { DateField } from "./form";
import { DateHelper } from "../../common/src";

interface PeriodSelectorProps {
    firstAvailableStartDate: string;
    /** End date the presets are anchored to. Defaults to the previous weekday. */
    defaultEndDate?: string;
    /** Initial start date when the URL has none. Defaults to `firstAvailableStartDate` (All). */
    defaultStartDate?: string;
    startDateKey?: string;
    endDateKey?: string;
    className?: string;
}

interface PresetButtonProps {
    label: string;
    onClick: () => void;
    active?: boolean;
}

const PresetButton = ({ label, onClick, active = false }: PresetButtonProps): React.ReactElement => {
    const base =
        "px-3 py-1 rounded-full border text-sm transition-colors duration-150 hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300";
    const activeCls = active ? " bg-blue-100 border-blue-400 text-blue-700 font-medium" : " bg-white border-gray-300 text-gray-700";
    return (
        <button type="button" className={base + activeCls} onClick={onClick}>
            {label}
        </button>
    );
};

interface RangeChipProps {
    startDate: string;
    endDate: string;
    onChangeStart: (v: string) => void;
    onChangeEnd: (v: string) => void;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const RangeChip = ({ startDate, endDate, onChangeStart, onChangeEnd }: RangeChipProps): React.ReactElement => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const chipBase =
        "inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-300 tabular-nums";
    const chipState = open
        ? " bg-blue-100 border-blue-400 text-blue-700"
        : " bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300";

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className={chipBase + chipState}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <CalendarDays size={14} className="text-slate-500" aria-hidden />
                <span>
                    {startDate} <span className="text-slate-400 px-1">→</span> {endDate}
                </span>
            </button>
            {open ? (
                <div
                    role="dialog"
                    className="absolute left-0 top-full mt-2 z-30 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
                >
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Custom range</div>
                    <Formik
                        enableReinitialize
                        initialValues={{ startDate, endDate }}
                        onSubmit={() => {
                            /* No submit button */
                        }}
                        validate={(values) => {
                            if (values.startDate && DATE_RE.test(values.startDate) && values.startDate !== startDate) {
                                onChangeStart(values.startDate);
                            }
                            if (values.endDate && DATE_RE.test(values.endDate) && values.endDate !== endDate) {
                                onChangeEnd(values.endDate);
                            }
                            return {};
                        }}
                    >
                        {() => (
                            <Form autoComplete="off" className="flex flex-col gap-2 mb-0">
                                <DateField className="mb-0" name="startDate" label="From" disabled={false} />
                                <DateField className="mb-0" name="endDate" label="To" disabled={false} />
                            </Form>
                        )}
                    </Formik>
                    <div className="mt-2 flex justify-end">
                        <button
                            type="button"
                            className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                            onClick={() => setOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

/**
 * Preset period buttons (Week, Month, YTD, ...) plus a custom range chip. The selected period is kept
 * in the query string so it survives reloads and can be shared, which also means the owning page reads
 * it back with `useQueryState` using the same keys and defaults.
 */
export const PeriodSelector = ({
    firstAvailableStartDate,
    defaultEndDate,
    defaultStartDate,
    startDateKey = "startDate",
    endDateKey = "endDate",
    className = "flex flex-wrap items-center gap-2 mb-0"
}: PeriodSelectorProps): React.ReactElement => {
    const anchorEndDate: string = defaultEndDate ?? DateHelper.dateToString(DateHelper.previousWeekday());

    const [_startDate, _setStartDate] = useQueryState(
        startDateKey,
        defaultStartDate || firstAvailableStartDate || "2020-01-01"
    );
    const [_endDate, _setEndDate] = useQueryState(endDateKey, anchorEndDate);
    const [, setQueryArgs] = useSearchParams();

    // Write both params in a single navigation. Calling the two `useQueryState` setters separately would
    // issue two synchronous URL updates in the same tick where the second can clobber the first, so when
    // the user has manually changed the end date, clicking a preset would fail to reset the end date.
    const setBoth = (newStart: string, newEnd: string) => {
        setQueryArgs((prev) => {
            prev.set(startDateKey, newStart);
            prev.set(endDateKey, newEnd);
            return prev;
        });
    };

    const presets: { id: string; label: string; computeStart: (end: string) => string }[] = [
        { id: "week", label: "Week", computeStart: (end) => DateHelper.addDays(end, -7) },
        { id: "month", label: "Month", computeStart: (end) => DateHelper.addMonths(end, -1) },
        {
            id: "ytd",
            label: "YTD",
            computeStart: (end) => `${DateHelper.getYear(end) - 1}-12-31`
        },
        { id: "year", label: "1 year", computeStart: (end) => DateHelper.addYears(end, -1) },
        { id: "5years", label: "5 years", computeStart: (end) => DateHelper.addYears(end, -5) },
        { id: "all", label: "All", computeStart: () => firstAvailableStartDate }
    ];

    // Detect which preset matches the current selection so we can highlight it.
    const activePresetId = (() => {
        if (_endDate !== anchorEndDate) return null;
        for (const p of presets) {
            if (p.computeStart(anchorEndDate) === _startDate) return p.id;
        }
        return null;
    })();

    return (
        <div className={className}>
            {presets.map((p) => (
                <PresetButton
                    key={p.id}
                    label={p.label}
                    active={activePresetId === p.id}
                    onClick={() => {
                        const newEnd = anchorEndDate;
                        const newStart = p.computeStart(newEnd);
                        if (!newStart) return;
                        setBoth(newStart, newEnd);
                    }}
                />
            ))}
            <RangeChip startDate={_startDate} endDate={_endDate} onChangeStart={_setStartDate} onChangeEnd={_setEndDate} />
        </div>
    );
};
